import axios from 'axios';
import type { AxiosRequestConfig } from 'axios';
import { message, notification } from 'antd';
import router from '@/router/index';
import { useAuthStore } from '@/store/auth';

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  success: boolean;
  data: any;
  errorCode?: number;
  errorMessage?: string;
  showType?: ErrorShowType;
}

type RetryableRequestConfig = AxiosRequestConfig & { _retry?: boolean };

const request = axios.create({ baseURL: '' });

request.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/** 清空登录态并跳转到登录页（accessToken、refreshToken 均已失效时使用） */
const forceLogout = () => {
  const { setAccessToken, setRefreshToken, setCurrentUser, setCurrentProject } =
    useAuthStore.getState();
  setAccessToken('');
  setRefreshToken('');
  setCurrentUser(null);
  setCurrentProject(null);
  router.navigate('/user/login');
};

// isRefreshing 锁：防止多个接口并发 401 时重复刷新 accessToken
let isRefreshing = false;
// 刷新期间等待的请求队列，刷新完成后统一通知（token 为 null 表示刷新失败）
let refreshSubscribers: Array<(token: string | null) => void> = [];

const subscribeTokenRefresh = (callback: (token: string | null) => void) => {
  refreshSubscribers.push(callback);
};

const onTokenRefreshed = (token: string | null) => {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
};

request.interceptors.response.use(
  (response) => {
    const data = response.data as ResponseStructure;
    if (data?.success === false) {
      message.error(data.errorMessage || '请求失败！');
    }
    return response.data;
  },
  async (error) => {
    const originalRequest = error.config as RetryableRequestConfig | undefined;

    if (error.response?.status === 401 && originalRequest) {
      const url = originalRequest.url || '';
      const isLoginRequest = url.includes('/api/auth/login');
      const isRefreshRequest = url.includes('/api/auth/refresh');

      // 登录接口本身返回 401（账号或密码错误等），交由登录页面处理提示，不走刷新流程
      if (isLoginRequest) {
        return Promise.reject(error);
      }

      // accessToken 已过期：使用 refreshToken 换取新 token 后重试原请求
      if (!originalRequest._retry && !isRefreshRequest) {
        originalRequest._retry = true;
        const refreshToken = localStorage.getItem('refresh_token');

        if (refreshToken) {
          if (isRefreshing) {
            // 已有刷新请求在进行中，排队等待其结果，保证只刷新一次
            return new Promise((resolve, reject) => {
              subscribeTokenRefresh((newToken) => {
                if (!newToken) {
                  reject(error);
                  return;
                }
                originalRequest.headers = originalRequest.headers || {};
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                resolve(request(originalRequest));
              });
            });
          }

          isRefreshing = true;
          try {
            const { data: refreshData } = await axios.post(
              '/api/auth/refresh',
              {},
              { headers: { Authorization: `Bearer ${refreshToken}` } },
            );
            const newAccessToken = refreshData.access_token;
            const newRefreshToken = refreshData.refresh_token;
            useAuthStore.getState().setAccessToken(newAccessToken);
            // 保存轮换后的 refresh_token，使过期时间滑动续期
            if (newRefreshToken) {
              useAuthStore.getState().setRefreshToken(newRefreshToken);
            }
            onTokenRefreshed(newAccessToken);

            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            return request(originalRequest);
          } catch (refreshError) {
            onTokenRefreshed(null);
            forceLogout();
            return Promise.reject(error);
          } finally {
            isRefreshing = false;
          }
        }

        // 没有 refreshToken，登录态已失效
        forceLogout();
        return Promise.reject(error);
      }

      // 重试后依然 401，或刷新接口自身返回 401：refreshToken 也已失效，登出并跳转登录页
      forceLogout();
      return Promise.reject(error);
    }

    const { skipErrorHandler = false } = (originalRequest?.fetchOptions as any) || {};
    if (skipErrorHandler) return Promise.reject(error);

    if (error.name === 'BizError') {
      const errorInfo: ResponseStructure | undefined = error.info;
      if (errorInfo) {
        const { errorMessage, errorCode } = errorInfo;
        switch (errorInfo.showType) {
          case ErrorShowType.SILENT:
            break;
          case ErrorShowType.WARN_MESSAGE:
            message.warning(errorMessage);
            break;
          case ErrorShowType.ERROR_MESSAGE:
            message.error(errorMessage);
            break;
          case ErrorShowType.NOTIFICATION:
            notification.open({ description: errorMessage, message: errorCode });
            break;
          case ErrorShowType.REDIRECT:
            // TODO: redirect
            break;
          default:
            message.error(errorMessage);
        }
      }
    } else if (error.response) {
      const { status, data } = error.response;
      const msg = data?.message || data?.errorMessage;
      if (msg) {
        message.error(msg);
      } else {
        message.error(`请求错误（${status}）`);
      }
    } else if (error.request) {
      message.error('网络无响应，请检查网络后重试');
    } else {
      message.error('请求发送失败，请重试');
    }

    return Promise.reject(error);
  },
);

export default request;
